import type { FlowConfig, FlowScreenNames } from '@/components/SimplifiedSignup/Context/types';
import type { PaymentScreenConfig } from '@/components/SimplifiedSignup/Payment/types';

import type { CheckoutSessionsFlowOptions } from './types';

// This flow never renders through `ActiveScreen`, so `component` and `heading` are never invoked.
const noopComponent: PaymentScreenConfig['component'] = () => null;
const noopHeading: PaymentScreenConfig['heading'] = () => ({ message: '' });

const FLOW_SCREEN_NAMES: FlowScreenNames[] = ['account', 'goals', 'handoff', 'payment', 'redeem', 'error'];

/**
 * Builds a single-step SimplifiedSignupContext flow whose only screen is payment. The provider's
 * routing effect still runs, and may route to `account` (no user) or `error`. Every screen key
 * therefore maps to the same payment config, so the active screen config never goes empty.
 */
export const buildCheckoutSessionsFlowConfig = ({
	flowName,
	plan,
	pageName = flowName,
	hasFreeTrial = false,
	paymentScreenConfig,
}: CheckoutSessionsFlowOptions): FlowConfig => {
	const screenConfig: PaymentScreenConfig = {
		component: noopComponent,
		heading: noopHeading,
		name: 'payment',
		nextScreen: null,
		showHeader: false,
		showToggle: false,
		...paymentScreenConfig,
	};

	return {
		activeScreenKey: 'payment',
		activeScreenConfig: screenConfig,
		name: flowName,
		pageName,
		plan,
		hasFreeTrial,
		screens: Object.fromEntries(FLOW_SCREEN_NAMES.map(screenName => [screenName, screenConfig])),
	};
};
